import {Contact, School} from "models"
import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AdminButton from "components/admin/button"
import AdminInput from "components/admin/input"
import {AdminSelectCountries} from "components/countries/select"
import {AdminSelectCountryStates} from "components/country-states/select"
import AppHistory from "shared/history"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import inflection from "inflection"
import Locales from "shared/locales"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import translatedAttributes from "@kaspernj/api-maker/build/translated-attributes.js"
import {useAdminLayout} from "components/admin/layout"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import {View} from "react-native"

/** @typedef {object} RoutesAdminSchoolsEditProps */

/**
 * @typedef {object} RoutesAdminSchoolsEditState
 * @property {unknown} form
 */

/**
 * @extends {ShapeComponent<RoutesAdminSchoolsEditProps, RoutesAdminSchoolsEditState>}
 */
class RoutesAdminSchoolsEdit extends ShapeComponent {
  /** @type {RoutesAdminSchoolsEditState} */
  state = {
    form: null
  }

  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.schools.edit"})
    const selectedSchoolAttributes = [
      "id", "email", "name", "principalName"
    ].concat(translatedAttributes(["name"], Locales.availableLocales()))
    const params = useParams()
    const {school} = useModel(School, {
      match: {params},
      newIfNoId: true,
      preload: ["contact"],
      select: {
        Contact: ["countryId", "countryStateId", "id"],
        School: selectedSchoolAttributes
      }
    })
    const contact = useMemo(() => school?.contact() || new Contact(), [school?.id()])

    let headerTitle

    if (school?.isNewRecord()) {
      headerTitle = t(".add_new_school")
    } else if (school?.isPersisted()) {
      headerTitle = t(".edit_school", {name: school.name()})
    }

    this.contact = contact
    this.school = school
    this.t = t


    useAdminLayout()?.setState({active: "schools", headerTitle})
  }

  render() {
    const {contact, school} = this.tt

    return (
      <View dataSet={{route: "admin--schools--edit"}}>
        {school &&
          <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
            {Locales.availableLocales().map((locale) =>
              <AdminInput
                attribute={`name${inflection.camelize(locale)}`}
                containerProps={{style: {marginBottom: 15}}}
                key={`input-name-${locale}`}
                label={`${Contact.humanAttributeName("name")} (${Locales.labelForLocale(locale)})`}
                model={school}
              />
            )}
            <View style={{marginBottom: 15}}>
              <AdminSelectCountries
                attribute="countryId"
                id="school_country_id"
                label={School.humanAttributeName("country")}
                model={contact}
                name="school[contact_attributes][country_id]"
              />
            </View>
            <View style={{marginBottom: 15}}>
              <AdminSelectCountryStates
                attribute="countryStateId"
                id="school_country_state_id"
                label={School.humanAttributeName("countryState")}
                model={contact}
                name="school[contact_attributes][country_state_id]"
              />
            </View>
            <AdminInput attribute="email" containerProps={{style: {marginBottom: 15}}} model={school} />
            <AdminInput attribute="principalName" containerProps={{style: {marginBottom: 15}}} model={school} />
            <AdminButton primary save submit />
          </Form>
        }
      </View>
    )
  }

  onSubmit = async () => {
    const {contact, school, t} = this.tt
    const formData = this.s.form.asObject()

    if (contact.isPersisted()) {
      formData.school.contact_attributes.id = contact.id()
    }

    try {
      await school.saveRaw(formData)
      FlashMessage.success(t(".the_school_was_saved"))
      AppHistory.push(Routes.adminSchoolPath(school.id()))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}

export default memo(shapeComponent(RoutesAdminSchoolsEdit))
